import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, X } from "lucide-react";
import { ContentCard, ui } from "../../../components/ui";
import { valueLabel } from "../leadForm";

const ATTACHMENT_TYPES = ["BOQ", "Tender_Specification", "Make_List", "Other"];
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.xlsx,.docx";
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 20;

/** The file's content as base64, without the "data:<type>;base64," prefix. */
const readBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** The attachment body the API takes, built from a picked browser File. */
export const toAttachment = async ({ file, attachmentType }) => ({
  fileName: file.name,
  fileByte: await readBase64(file),
  attachmentType,
  fileSize: file.size,
  contentType: file.type,
});

/**
 * The lead's files. Each carries its type (BOQ, Tender Specification, Make
 * List or Other); PDF, JPG, PNG, XLSX and DOCX only, 10 MB each, 20 per lead.
 * Picked files wait in a pending list — removable — and are sent with the page's Save.
 */
export function LeadAttachments({ attachments, pending, editable, onAdd, onRemove }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [pickType, setPickType] = useState("");
  const [error, setError] = useState("");
  const saved = attachments ?? [];

  const pick = (type) => {
    setPickType(type);
    setError("");
    inputRef.current.click();
  };

  const stage = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    const extension = file.name.split(".").pop().toLowerCase();
    if (!ACCEPT.split(",").includes(`.${extension}`)) setError(t("leadManagement.attachmentsBadType"));
    else if (file.size > MAX_BYTES) setError(t("leadManagement.attachmentsTooLarge"));
    else if (saved.length + pending.length >= MAX_FILES) setError(t("leadManagement.attachmentsLimit"));
    else onAdd({ key: `${Date.now()}-${file.name}`, file, attachmentType: pickType });
  };

  const typeTag = (type) => (
    <span className="shrink-0 rounded-full bg-workspace px-2.5 py-1 text-[11px] font-semibold per-file-hint">
      {valueLabel(type)}
    </span>
  );

  return (
    <ContentCard
      title={t("leadManagement.attachmentsTitle")}
      bodyClassName="p-4 sm:p-5"
      action={
        editable ? (
          <div className="flex flex-wrap gap-2">
            {ATTACHMENT_TYPES.map((type) => (
              <button key={type} type="button" onClick={() => pick(type)} className={ui.filterBtn}>
                + {valueLabel(type)}
              </button>
            ))}
          </div>
        ) : null
      }
    >
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={stage} />

      {error ? <p className="mb-3 text-[12px] font-semibold text-rose-600">{error}</p> : null}

      {pending.length ? (
        <ul className="mb-4 divide-y divide-(--color-text-border)/35 rounded-xl border border-dashed border-primary/40 px-3">
          {pending.map((item) => (
            <li key={item.key} className="flex items-center justify-between gap-3 py-2">
              <span className="flex min-w-0 items-center gap-2 truncate text-sm font-semibold text-foreground">
                <FileText className="h-4 w-4 shrink-0 per-file-hint" />
                {item.file.name}
                <span className="text-[11px] font-semibold per-file-hint">{t("leadManagement.lotsUnsaved")}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {typeTag(item.attachmentType)}
                <button
                  type="button"
                  onClick={() => onRemove(item.key)}
                  title={t("leadManagement.attachmentsRemove")}
                  className="grid h-7 w-7 place-items-center rounded-lg border border-rose-300 text-rose-700 transition hover:bg-rose-50"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {saved.length === 0 && pending.length === 0 ? (
        <p className="text-[12px] per-file-hint">{t("leadManagement.attachmentsEmpty")}</p>
      ) : (
        <ul className="divide-y divide-(--color-text-border)/35">
          {saved.map((file, index) => (
            <li key={`${file.fileName}-${index}`} className="flex items-center justify-between gap-3 py-2.5">
              <a
                href={`data:${file.contentType};base64,${file.fileByte}`}
                download={file.fileName}
                className="flex min-w-0 items-center gap-2 truncate text-sm font-semibold text-primary hover:underline"
              >
                <FileText className="h-4 w-4 shrink-0" />
                {file.fileName}
              </a>
              {typeTag(file.attachmentType)}
            </li>
          ))}
        </ul>
      )}
    </ContentCard>
  );
}

export default LeadAttachments;
